import React, { ReactElement, useState } from "react";
import { Button, Col, Divider, Row, Typography } from "antd";
import {
  PlusCircleOutlined,
  EyeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import SupplierImg from "../../../assets/img/png/supplier.png";
import UsersImg from "../../../assets/img/png/users.png";
import CustomerImg from "../../../assets/img/png/customer.png";
import DocumentsImg from "../../../assets/img/png/documents.png";
import RepseImg from "../../../assets/img/png/repse.png";
import AddDocumentsImg from "../../../assets/img/png/add-document.png";

import Modal from "../../general/modal/index";
import CanShow from "../../can-show";

import UserForm from "../../../pages/administration/users/detail";
import SupplierForm from "../../../pages/administration/suppliers/detail";
import CustomerForm from "../../../pages/administration/customers/detail";
import ContractsForm from "../../../pages/home/contracts/detail";
import RepseForm from "../../../pages/home/repse";
import CommentsForm from "../../../pages/home/comments/detail";

import { Supplier, post, put } from "../../../api";
import { useUser } from "../../../hooks";

const { Title } = Typography;

type ModalType =
  | "usuario"
  | "proveedor"
  | "cliente"
  | "contrato"
  | "comentario"
  | "REPSE";

const CardsHome = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [titleModal, setTitleModal] = useState("");
  const [contextModal, setContextModal] = useState<ReactElement | null>(null);

  const closeOn200 = (r: { statusCode: number }) => {
    if (r.statusCode === 200) setModalIsVisible(false);
  };
  const openModal = (title: string, content: ReactElement) => {
    setTitleModal(title);
    setContextModal(content);
    setModalIsVisible(true);
  };

  const buildUserModal = () =>
    openModal(
      "Agregar usuario",
      <UserForm
        type="add"
        add={(usr) => post("/users", usr, true).then(closeOn200)}
        update={(usr) => put("/users", usr, true).then(closeOn200)}
      />,
    );

  const buildSupplierModal = () =>
    openModal(
      "Agregar proveedor",
      <SupplierForm
        type="add"
        add={(sup) => post("/suppliers", sup, true).then(closeOn200)}
        update={(sup) => put("/suppliers", sup, true).then(closeOn200)}
      />,
    );

  const buildCustomerModal = () =>
    openModal(
      "Agregar cliente",
      <CustomerForm
        type="add"
        add={(cus) => post("/customers", cus, true).then(closeOn200)}
        update={(cus) => put("/customers", cus, true).then(closeOn200)}
      />,
    );

  const buildContractModal = () =>
    openModal(
      "Agregar contrato",
      <ContractsForm
        type="add"
        add={(contract) => post("/contracts", contract, true).then(closeOn200)}
        update={(contract) =>
          put("/contracts", contract, true).then(closeOn200)
        }
      />,
    );

  const buildCommentModal = () =>
    openModal(
      "Agregar comentario",
      <CommentsForm
        type="add"
        add={(comment) => post("/comments", comment, true).then(closeOn200)}
        update={(comment) => put("/contracts", comment, true).then(closeOn200)}
      />,
    );

  const buildRepseModal = (typeForm: "add" | "edit") =>
    openModal(
      `${typeForm === "add" ? "Agrerar" : "Editar"} REPSE`,
      <RepseForm
        type={typeForm}
        supplierId={user ? (user.extendedInfo as Supplier).id : 0}
        add={() => setModalIsVisible(false)}
        update={() => {
          setModalIsVisible(false);
          setContextModal(null);
        }}
      />,
    );

  const showModal = (type: ModalType, typeForm?: "add" | "edit") => {
    switch (type) {
      case "usuario":
        return buildUserModal();
      case "proveedor":
        return buildSupplierModal();
      case "cliente":
        return buildCustomerModal();
      case "contrato":
        return buildContractModal();
      case "comentario":
        return buildCommentModal();
      case "REPSE":
        if (typeForm) buildRepseModal(typeForm);
        return;
    }
  };

  const AdminCard = ({
    img,
    title,
    actions,
  }: {
    img: string;
    title: string;
    actions: ReactElement;
  }) => (
    <div className="admin__container">
      <Col>
        <img width="100%" alt="" src={img} />
        <Title
          style={{ color: "black", marginTop: 10, textAlign: "center" }}
          level={5}
        >
          {title}
        </Title>
        <Divider />
      </Col>
      <Col style={{ justifyContent: "center", display: "flex" }}>{actions}</Col>
    </div>
  );

  return (
    <div>
      <Row gutter={16}>
        {/* REPSE (Supplier) */}
        <CanShow roles={["Supplier"]}>
          <>
            <Col lg={8} md={12} xs={24}>
              <AdminCard
                img={RepseImg}
                title="Archivos"
                actions={
                  <>
                    {user &&
                      user.role_id === 3 &&
                      (user.extendedInfo as Supplier).repse_id === null && (
                        <Button
                          onClick={() =>
                            navigate(
                              `/supplier/files/${user ? (user.extendedInfo as Supplier).id : 0}`,
                            )
                          }
                          className="btn-download"
                          icon={<PlusCircleOutlined />}
                          style={{ width: 120 }}
                        >
                          Agregar
                        </Button>
                      )}
                    {user &&
                      user.role_id === 3 &&
                      (user.extendedInfo as Supplier).repse_id !== null && (
                        <Button
                          onClick={() =>
                            navigate(
                              `/supplier/files/${user ? (user.extendedInfo as Supplier).id : 0}`,
                            )
                          }
                          className="btn-download"
                          icon={<EditOutlined />}
                          style={{ width: 120 }}
                        >
                          Editar
                        </Button>
                      )}
                  </>
                }
              />
            </Col>

            {/* Agregar comentario (Supplier) */}
            <Col lg={8} md={12} xs={24}>
              <AdminCard
                img={AddDocumentsImg}
                title="Agregar comentario"
                actions={
                  <Button
                    className="btn-download"
                    onClick={() => showModal("comentario")}
                    icon={<PlusCircleOutlined />}
                    style={{ width: 120 }}
                  >
                    Agregar
                  </Button>
                }
              />
            </Col>
          </>
        </CanShow>

        {/* Ver contratos (TODOS) */}
        <Col lg={8} md={12} xs={24}>
          <AdminCard
            img={DocumentsImg}
            title="Ver contratos"
            actions={
              <Button
                onClick={() => navigate("/contracts")}
                className="btn-download"
                icon={<EyeOutlined />}
                style={{ width: 120 }}
              >
                Visualizar
              </Button>
            }
          />
        </Col>

        {/* Agregar contrato */}
        <CanShow roles={["Admin", "Financial Advisor"]}>
          <Col lg={8} md={12} xs={24}>
            <AdminCard
              img={AddDocumentsImg}
              title="Agregar contrato"
              actions={
                <Button
                  className="btn-download"
                  onClick={() => showModal("contrato")}
                  icon={<PlusCircleOutlined />}
                  style={{ width: 120 }}
                >
                  Agregar
                </Button>
              }
            />
          </Col>
        </CanShow>

        {/* Proveedores (Customer) */}
        <CanShow roles={["Customer"]}>
          <>
            <Col lg={8} md={12} xs={24}>
              <AdminCard
                img={SupplierImg}
                title="Proveedores"
                actions={
                  <Row justify="end" style={{ justifyContent: "center" }}>
                    <Col style={{ display: "flex" }}>
                      <Button
                        onClick={() => showModal("proveedor")}
                        className="btn-download"
                        icon={<PlusCircleOutlined />}
                        style={{ width: 120 }}
                      >
                        Agregar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() => navigate("/administration/suppliers")}
                        className="btn-download"
                        icon={<EyeOutlined />}
                        style={{ width: 120 }}
                      >
                        Visualizar
                      </Button>
                    </Col>
                  </Row>
                }
              />
            </Col>
            <Col lg={8} md={12} xs={24}>
              <AdminCard
                img={UsersImg}
                title="Usuarios"
                actions={
                  <Row justify="end" style={{ justifyContent: "center" }}>
                    <Col style={{ display: "flex" }}>
                      <Button
                        onClick={() => showModal("usuario")}
                        className="btn-download"
                        icon={<PlusCircleOutlined />}
                        style={{ width: 120 }}
                      >
                        Agregar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() => navigate("/administration/users")}
                        className="btn-download"
                        icon={<EyeOutlined />}
                        style={{ width: 120 }}
                      >
                        Visualizar
                      </Button>
                    </Col>
                  </Row>
                }
              />
            </Col>
            {/* Agregar comentario (Customer) */}
            <Col lg={8} md={12} xs={24}>
              <AdminCard
                img={AddDocumentsImg}
                title="Agregar comentario"
                actions={
                  <Button
                    className="btn-download"
                    onClick={() => showModal("comentario")}
                    icon={<PlusCircleOutlined />}
                    style={{ width: 120 }}
                  >
                    Agregar
                  </Button>
                }
              />
            </Col>
          </>
        </CanShow>

        {/* Admin / Financial Advisor section */}
        <CanShow roles={["Admin", "Financial Advisor"]}>
          <>
            {/* Usuarios */}

            <Col lg={8} md={12} xs={24}>
              <AdminCard
                img={UsersImg}
                title="Usuarios"
                actions={
                  <Row justify="end" style={{ justifyContent: "center" }}>
                    <Col style={{ display: "flex" }}>
                      <Button
                        onClick={() => showModal("usuario")}
                        className="btn-download"
                        icon={<PlusCircleOutlined />}
                        style={{ width: 120 }}
                      >
                        Agregar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() => navigate("/administration/users")}
                        className="btn-download"
                        icon={<EyeOutlined />}
                        style={{ width: 120 }}
                      >
                        Visualizar
                      </Button>
                    </Col>
                  </Row>
                }
              />
            </Col>

            {/* Proveedores */}
            <Col lg={8} md={12} xs={24}>
              <AdminCard
                img={SupplierImg}
                title="Proveedores"
                actions={
                  <Row justify="end" style={{ justifyContent: "center" }}>
                    <Col style={{ display: "flex" }}>
                      <Button
                        onClick={() => showModal("proveedor")}
                        className="btn-download"
                        icon={<PlusCircleOutlined />}
                        style={{ width: 120 }}
                      >
                        Agregar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() => navigate("/administration/suppliers")}
                        className="btn-download"
                        icon={<EyeOutlined />}
                        style={{ width: 120 }}
                      >
                        Visualizar
                      </Button>
                    </Col>
                  </Row>
                }
              />
            </Col>

            {/* Clientes */}
            {!user?.customer_id && (
              <Col lg={8} md={12} xs={24}>
                <AdminCard
                  img={CustomerImg}
                  title="Clientes"
                  actions={
                    <Row justify="end" style={{ justifyContent: "center" }}>
                      <Col style={{ display: "flex" }}>
                        <Button
                          onClick={() => showModal("cliente")}
                          className="btn-download"
                          icon={<PlusCircleOutlined />}
                          style={{ width: 120 }}
                        >
                          Agregar
                        </Button>
                      </Col>
                      <Col>
                        <Button
                          onClick={() => navigate("/administration/customers")}
                          className="btn-download"
                          icon={<EyeOutlined />}
                          style={{ width: 120 }}
                        >
                          Visualizar
                        </Button>
                      </Col>
                    </Row>
                  }
                />
              </Col>
            )}
          </>
        </CanShow>
      </Row>

      <Modal
        title={titleModal}
        isVisible={modalIsVisible}
        setIsVisible={setModalIsVisible}
        width={1000}
      >
        {contextModal}
      </Modal>
    </div>
  );
};

export default CardsHome;
